import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Mail,
  MapPin,
  Briefcase,
  Building2,
  Calendar,
  Sparkles,
  Loader2,
  TrendingUp,
  Star,
  ShieldAlert,
  ArrowUpRight,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import CareerTimeline from "@/components/CareerTimeline";
import { toast } from "sonner";

export default function Profile({ self }) {
  const params = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [lifecycle, setLifecycle] = useState([]);
  const [team, setTeam] = useState([]);
  const [manager, setManager] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [genLoading, setGenLoading] = useState(false);

  const targetId = self ? user?.employee_id : params.id;

  useEffect(() => {
    if (self && !user?.employee_id) {
      // Logged-in account isn't tied to an employee record
      setLoading(false);
      return;
    }
    if (!targetId) return;
    let m = true;
    setLoading(true);
    Promise.all([
      api.get(`/employees/${targetId}`),
      api.get(`/employees/${targetId}/lifecycle`),
      api.get(`/employees/${targetId}/performance`),
      api.get(`/employees/${targetId}/team`),
    ])
      .then(([e, lc, ph, tm]) => {
        if (!m) return;
        setEmployee(e.data);
        setLifecycle(lc.data.items || []);
        setHistory(ph.data.items || []);
        setTeam(tm.data.items || []);
        if (e.data.reporting_manager_id) {
          api
            .get(`/employees/${e.data.reporting_manager_id}`)
            .then((r) => m && setManager(r.data))
            .catch(() => {});
        } else {
          setManager(null);
        }
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load profile"))
      .finally(() => m && setLoading(false));
    return () => {
      m = false;
    };
  }, [targetId, self, user?.employee_id]);

  const trendData = useMemo(() => {
    return [...history]
      .sort((a, b) => a.cycle_year - b.cycle_year)
      .map((h) => ({ year: h.cycle_year, rating: h.overall_rating_value, label: h.overall_rating_label }));
  }, [history]);

  const generateNarrative = async () => {
    setGenLoading(true);
    try {
      const r = await api.post("/ai/career-narrative", { employee_id: employee.id });
      setNarrative(r.data.narrative);
      toast.success("Career narrative generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI temporarily unavailable");
    } finally {
      setGenLoading(false);
    }
  };

  if (self && !user?.employee_id) {
    return (
      <div className="max-w-2xl axia-card p-8" data-testid="profile-no-employee">
        <ShieldAlert className="w-6 h-6 text-amber-500" />
        <h2 className="font-display text-xl font-semibold mt-3 text-slate-900">No employee record linked</h2>
        <p className="text-sm text-slate-500 mt-2">
          Your sign-in account isn't yet linked to an employee in your workspace. Ask your HR admin to associate your account with an employee record.
        </p>
      </div>
    );
  }
  if (loading) return <div className="text-sm text-slate-500" data-testid="profile-loading">Loading profile…</div>;
  if (!employee) return <div className="text-sm text-slate-500">Employee not found.</div>;

  const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  const initials = fullName.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const isAdmin = hasRole(user, "tenant_admin", "hr_admin", "hrbp");
  const canSeePerformance = isAdmin || user?.employee_id === employee.id || user?.employee_id === employee.reporting_manager_id;

  return (
    <div className="max-w-7xl space-y-6" data-testid="profile-page">
      {/* Header */}
      <div className="axia-card p-6 lg:p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 axia-grid-bg opacity-50 pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row gap-6 lg:items-center">
          <div className="w-24 h-24 rounded-xl bg-slate-900 text-white flex items-center justify-center font-display text-2xl font-bold tracking-tight">
            {initials}
          </div>
          <div className="flex-1">
            <div className="axia-overline">{employee.employment_status?.toUpperCase()}</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">{fullName}</h1>
            <div className="text-sm text-slate-600 mt-1">{employee.designation || "—"}</div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> {employee.job_grade_band || "—"}</span>
              <span className="inline-flex items-center gap-1.5"><Building2 className="w-3 h-3" /> {employee.department || "—"}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {[employee.location_city, employee.location_country].filter(Boolean).join(", ") || "—"}</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Joined {employee.date_of_joining || "—"}</span>
              <span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> {employee.email}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={generateNarrative}
              disabled={genLoading || history.length === 0}
              data-testid="profile-generate-narrative"
            >
              {genLoading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-2" />}
              AI career narrative
            </Button>
            {manager && (
              <Link
                to={`/profile/${manager.id}`}
                className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 mt-1"
                data-testid="profile-manager-link"
              >
                Reports to <span className="font-medium text-slate-700">{manager.first_name} {manager.last_name}</span> <ArrowUpRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
        {narrative && (
          <div className="mt-6 bg-slate-50 border border-slate-200 rounded-md p-5" data-testid="profile-narrative">
            <div className="axia-overline text-slate-500 mb-2 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Draft by Axia AI · edit before using</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{narrative}</p>
          </div>
        )}
      </div>

      <Tabs defaultValue="overview" data-testid="profile-tabs">
        <TabsList className="bg-transparent gap-2 p-0 border-b border-slate-200 rounded-none w-full justify-start h-auto">
          {[
            { v: "overview", label: "Overview" },
            { v: "reporting", label: "Reporting" },
            { v: "performance", label: "Performance History" },
            { v: "feedback", label: "Feedback" },
            { v: "development", label: "Development" },
            { v: "timeline", label: "Career Timeline" },
          ].map((t) => (
            <TabsTrigger
              key={t.v}
              value={t.v}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900 data-[state=active]:text-slate-900 text-slate-500 px-3 pb-3 pt-2 text-sm bg-transparent shadow-none data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              data-testid={`profile-tab-${t.v}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <KV label="Employee ID" value={employee.employee_id} />
            <KV label="Email" value={employee.email} />
            <KV label="Designation" value={employee.designation} />
            <KV label="Department" value={employee.department} />
            <KV label="Business Unit" value={employee.business_unit} />
            <KV label="Job grade band" value={employee.job_grade_band} />
            <KV label="Job level" value={employee.job_level} />
            <KV label="Employment type" value={employee.employment_type} />
            <KV label="Location" value={[employee.location_city, employee.location_country].filter(Boolean).join(", ") || "—"} />
            <KV label="Date of joining" value={employee.date_of_joining} />
            <KV label="Date of confirmation" value={employee.date_of_confirmation || "—"} />
            <KV label="Status" value={employee.employment_status} />
          </div>
        </TabsContent>

        <TabsContent value="reporting" className="pt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="axia-card p-6">
              <div className="axia-overline mb-3">Reporting line</div>
              {manager ? (
                <Link to={`/profile/${manager.id}`} className="block px-4 py-3 border border-slate-200 rounded-md hover:border-slate-400">
                  <div className="text-xs text-slate-500">Reports to</div>
                  <div className="font-display text-base font-semibold mt-0.5">{manager.first_name} {manager.last_name}</div>
                  <div className="text-xs text-slate-500">{manager.designation}</div>
                </Link>
              ) : (
                <div className="text-sm text-slate-400">No manager assigned.</div>
              )}
            </div>
            <div className="axia-card p-6">
              <div className="axia-overline mb-3">Direct reports ({team.length})</div>
              {team.length === 0 ? (
                <div className="text-sm text-slate-400">No direct reports.</div>
              ) : (
                <ul className="space-y-2">
                  {team.map((t) => (
                    <li key={t.id}>
                      <Link to={`/profile/${t.id}`} className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 border border-slate-100">
                        <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-semibold">
                          {(t.first_name?.[0] || "") + (t.last_name?.[0] || "")}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-900 truncate">{t.first_name} {t.last_name}</div>
                          <div className="text-xs text-slate-500 truncate">{t.designation}</div>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="pt-6">
          {!canSeePerformance ? (
            <div className="text-sm text-slate-500">Performance history is private.</div>
          ) : history.length === 0 ? (
            <div className="text-sm text-slate-500">No performance history yet. Historical ratings can be imported by HR.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="axia-card p-6 lg:col-span-2">
                <div className="flex items-center justify-between">
                  <div className="axia-overline">Rating trend</div>
                  <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
                </div>
                <div className="h-56 mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                      <XAxis dataKey="year" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis domain={[1, 5]} stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                      <Tooltip
                        contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                        labelStyle={{ color: "#0F172A", fontWeight: 600 }}
                      />
                      <ReferenceLine y={3} stroke="#E2E8F0" strokeDasharray="4 4" />
                      <Line type="monotone" dataKey="rating" stroke="#0F172A" strokeWidth={2.5} dot={{ r: 4, fill: "#0F172A" }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="axia-card p-6 space-y-3">
                <div className="axia-overline">Cycles</div>
                {history.map((h) => (
                  <div key={h.id} className="border border-slate-100 rounded-md p-3" data-testid={`history-row-${h.cycle_year}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-slate-900">{h.cycle_name}</div>
                      <div className="inline-flex items-center gap-1 text-xs font-mono text-slate-700">
                        <Star className="w-3 h-3 text-amber-500" /> {h.overall_rating_value}
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {h.framework_name} · {h.overall_rating_label}
                      {h.calibration_adjusted && <span className="ml-2 text-violet-700">calibration adj.</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="feedback" className="pt-6">
          <ProfileFeedbackTab employeeId={employee.id} />
        </TabsContent>

        <TabsContent value="development" className="pt-6">
          <ProfileDevelopmentTab employeeId={employee.id} />
        </TabsContent>

        <TabsContent value="timeline" className="pt-6">
          <CareerTimeline events={lifecycle} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="axia-card p-4">
      <div className="axia-overline">{label}</div>
      <div className="mt-1.5 text-sm font-medium text-slate-900 break-words">{value || "—"}</div>
    </div>
  );
}

function ProfileFeedbackTab({ employeeId }) {
  const [kudos, setKudos] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/kudos/received", { params: { employee_id: employeeId } }).catch(() => ({ data: { items: [] } })),
      api.get("/feedback360/received", { params: { employee_id: employeeId } }).catch(() => ({ data: { items: [] } })),
    ]).then(([k, f]) => {
      setKudos(k.data.items || []);
      setFeedback(f.data.items || []);
    }).finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="profile-feedback-tab">
      <section className="axia-card p-6">
        <h2 className="font-display font-semibold text-slate-900">Kudos received ({kudos.length})</h2>
        <ul className="mt-4 space-y-3">
          {kudos.length === 0 && <li className="text-sm text-slate-400">No kudos yet.</li>}
          {kudos.map((k) => (
            <li key={k.id} className="text-sm border-l-2 border-emerald-200 pl-3">
              <div className="text-xs text-slate-500">From {k.giver?.name}</div>
              <p className="text-slate-700 mt-0.5">{k.message}</p>
            </li>
          ))}
        </ul>
      </section>
      <section className="axia-card p-6">
        <h2 className="font-display font-semibold text-slate-900">Peer feedback ({feedback.length})</h2>
        <ul className="mt-4 space-y-3">
          {feedback.length === 0 && <li className="text-sm text-slate-400">No 360 feedback yet.</li>}
          {feedback.map((r) => (
            <li key={r.id} className="text-sm border-l-2 border-violet-200 pl-3">
              <div className="text-xs text-slate-500">From {r.peer_name}</div>
              {(r.answers || []).slice(0, 1).map((a, i) => (
                <p key={i} className="text-slate-700 mt-0.5 line-clamp-3">{a.answer}</p>
              ))}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ProfileDevelopmentTab({ employeeId }) {
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/check-ins", { params: { employee_id: employeeId } })
      .then((r) => setCheckIns((r.data.items || []).slice(0, 10)))
      .catch(() => setCheckIns([]))
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="axia-card p-6" data-testid="profile-development-tab">
      <h2 className="font-display font-semibold text-slate-900">Recent check-ins</h2>
      <ul className="mt-4 space-y-3">
        {checkIns.length === 0 && <li className="text-sm text-slate-400">No check-ins logged yet.</li>}
        {checkIns.map((c) => (
          <li key={c.id} className="text-sm border-l-2 border-slate-200 pl-3">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <span className="font-mono">{c.progress_pct}%</span> · <span className="capitalize">{c.status.replace(/_/g, " ")}</span>
            </div>
            <p className="text-slate-700 mt-0.5">{c.commentary || <span className="text-slate-400">No commentary</span>}</p>
            {c.blockers && <div className="text-xs text-rose-700 mt-1">Blocker: {c.blockers}</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}
